import { createGlobalStyle } from 'styled-components';

/**
 * FB2-13: keep every view-order modal's purple header (order ID) and the close
 * (X) button pinned to the top while the modal body scrolls. The UDS <Modal>
 * renders the heading and close button inside the same scrolling container, so
 * by default they scroll away. Mount this scoped global style only while the
 * modal is open (only one modal can be open at a time, so the [role="dialog"]
 * selector effectively targets just the active dialog). It pins both elements
 * with position: sticky and removes the scroll container's top padding so the
 * header sits flush.
 *
 * Usage: render `{open && <StickyOrderModalHeader />}` inside the modal wrapper.
 */
const StickyOrderModalHeader = createGlobalStyle`
  /* Modal content wrapper: by default UDS flex-shrinks it to the scroll
     container height while its children overflow, which caps the heading's
     sticky range. Let it grow to its full content height so the heading stays
     pinned for the entire scroll. */
  [role="dialog"] div:has(> div > h3[role="heading"]) {
    flex-shrink: 0 !important;
    flex-basis: auto !important;
    min-height: auto !important;
  }
  /* Purple heading bar (order ID).
     UDS Modal's own heading defaults to headingLevel="h3" too, so a naive
     div:has(> h3[role="heading"]) selector also matches unrelated h3 Typography
     rendered inside the modal BODY (e.g. "Total Contract Value" in
     orderSummary/wls.js), painting that content's wrapper div purple as well
     (FB2-ish regression). Distinguish them: body headings use Typography's
     block prop, which wraps the text in an extra inner div inside the h3; the
     Modal's own (non-block) heading renders the h3 with a bare text node.
     Exclude the block case. */
  [role="dialog"] div:has(> h3[role="heading"]):not(:has(> h3[role="heading"] > div)) {
    position: sticky !important;
    top: 0 !important;
    z-index: 3 !important;
    background-color: #4b286d !important;
    border-top-left-radius: 10px !important;
    border-top-right-radius: 10px !important;
    min-height: 56px !important;
    justify-content: center !important;
  }
  /* Heading text: force white (UDS default heading colour is dark grey, which is
     unreadable on the purple bar). */
  [role="dialog"] div:has(> h3[role="heading"]):not(:has(> h3[role="heading"] > div)) > h3[role="heading"] {
    color: #ffffff !important;
  }
  /* Close (X) icon: force white to match the heading text. Scope strictly to the
     modal-header close button (the only close wrapper with a following sibling
     containing the modal heading) so the FileUpload notification's dismiss
     button — which also has aria-label="Close" — keeps its dark icon and stays
     visible against the light notification background. */
  [role="dialog"] div:has(> button[aria-label="Close"]):has(~ div h3[role="heading"]) svg,
  [role="dialog"] div:has(> button[aria-label="Close"]):has(~ div h3[role="heading"]) svg * {
    fill: #ffffff !important;
    color: #ffffff !important;
  }
  /* Any other close (X) inside the dialog that is NOT the modal header — e.g. the
     FileUpload success/error notification's dismiss button — renders white by
     default and blends into the light notification background. Force it dark so
     it stays visible. */
  [role="dialog"] div:has(> button[aria-label="Close"]):not(:has(~ div h3[role="heading"])) svg,
  [role="dialog"] div:has(> button[aria-label="Close"]):not(:has(~ div h3[role="heading"])) svg * {
    fill: #2a2c2e !important;
    color: #2a2c2e !important;
  }
  /* Close (X) button: pin it and vertically centre it within the 56px header.
     Scope strictly to the modal-header close button (the only close wrapper that
     has a following sibling containing the modal heading) so we don't disturb
     other close buttons rendered inside the modal body — e.g. the FileUpload
     success/error notification's dismiss button, which also has
     aria-label="Close". */
  [role="dialog"] div:has(> button[aria-label="Close"]):has(~ div h3[role="heading"]) {
    position: sticky !important;
    top: 0 !important;
    align-self: flex-end !important;
    height: 56px !important;
    justify-content: center !important;
    margin-bottom: -56px !important;
    padding-top: 0 !important;
    z-index: 4 !important;
  }
  [role="dialog"] div:has(> div > div > h3[role="heading"]) {
    padding-top: 0 !important;
  }
`;

export default StickyOrderModalHeader;
